import { useMemo } from 'react';
import { Bot, Sparkles, BookOpen, CheckCircle } from 'lucide-react';
import { sanitizeMessageHtml } from '@centraoneplus/ui/src/security/sanitize-html';

interface AIResponseCardProps {
  content: string;
}

interface ParsedContent {
  greeting: string;
  intro: string;
  mainContent: string;
  relatedArticles: Array<{ title: string; url?: string }>;
  citations: string[];
  closing: string;
}

function parseContent(html: string): ParsedContent {
  const result: ParsedContent = {
    greeting: '',
    intro: '',
    mainContent: '',
    relatedArticles: [],
    citations: [],
    closing: ''
  };

  const temp = document.createElement('div');
  temp.innerHTML = html;

  const children = Array.from(temp.children);

  children.forEach((child) => {
    const text = child.textContent?.trim() || '';
    const tagName = child.tagName.toLowerCase();

    if (tagName === 'p') {
      if (text.match(/^(hi|hello|dear)\s/i)) {
        result.greeting = text;
      } else if (text.toLowerCase().includes('thank you') || text.toLowerCase().includes('based on')) {
        result.intro = text;
      } else if (text.toLowerCase().includes('related articles')) {
        // Skip
      } else if (text.toLowerCase().includes('further questions') || text.toLowerCase().includes('feel free')) {
        result.closing = text;
      }
    } else if (tagName === 'div') {
      result.mainContent = child.innerHTML;
    } else if (tagName === 'ul' || tagName === 'ol') {
      const items = child.querySelectorAll('li');
      result.relatedArticles = Array.from(items).map(li => {
        const link = li.querySelector('a');
        return {
          title: link?.textContent || li.textContent || '',
          url: link?.href
        };
      });
    }
  });

  const citationMatches = html.match(/\[(\d+)\]/g);
  if (citationMatches) {
    result.citations = [...new Set(citationMatches.map(c => c.replace(/[\[\]]/g, '')))];
  }

  if (!result.mainContent && !result.greeting) {
    result.mainContent = html;
  }

  return result;
}

function formatContent(html: string): string {
  return html
    .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '</p><p>')
    .replace(/\s*\[\d+\]\s*/g, ' ')
    .trim();
}

export function AIResponseCard({ content }: AIResponseCardProps) {
  const sections = useMemo(() => parseContent(content), [content]);

  return (
    <div className="space-y-4">
      {/* Greeting */}
      {sections.greeting && (
        <p className="text-gray-700 dark:text-gray-200">{sections.greeting}</p>
      )}

      {/* AI Badge */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full">
          <Sparkles className="h-3.5 w-3.5 text-purple-600" />
          <span className="text-xs font-semibold text-purple-700">AI Powered Response</span>
        </span>
      </div>

      {/* Introduction */}
      {sections.intro && (
        <p className="text-sm text-gray-600 dark:text-gray-400">{sections.intro}</p>
      )}

      {/* Main Content Card */}
      {sections.mainContent && (
        <div className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm dark:bg-gray-800 dark:border-gray-700">
          {/* Card Header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 border-b border-gray-100 dark:border-gray-800">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Knowledge Base Answer</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">Based on your inquiry</p>
            </div>
          </div>

          {/* Card Content */}
          <div className="p-4">
            <div
              className="text-sm text-gray-700 leading-relaxed space-y-3
                [&>p]:mb-0
                [&_br]:block [&_br]:h-2"
              dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(formatContent(sections.mainContent)) }}
            />
          </div>

          {/* Citation Footer */}
          {sections.citations.length > 0 && (
            <div className="px-4 py-3 bg-green-50 border-t border-green-100">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-xs font-medium text-green-700">
                  Verified from {sections.citations.length} knowledge base source{sections.citations.length > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Related Articles */}
      {sections.relatedArticles.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="h-4 w-4 text-blue-600" />
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Related Articles</h4>
          </div>
          <div className="space-y-2">
            {sections.relatedArticles.map((article, idx) => (
              <a
                key={idx}
                href={article.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100 hover:bg-blue-100 hover:border-blue-200 transition-all group"
              >
                <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-white">{idx + 1}</span>
                </div>
                <span className="text-sm text-blue-700 font-medium group-hover:text-blue-800 group-hover:underline">
                  {article.title}
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Closing */}
      {sections.closing && (
        <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
          <p className="text-sm text-gray-500 italic dark:text-gray-400">{sections.closing}</p>
        </div>
      )}
    </div>
  );
}

export default AIResponseCard;
